/**
 * Family Mastery Engine
 *
 * Tracks mastery per familyId using an ELO-inspired continuous scoring model.
 * Runs in parallel with the per-question knowledge state engine.
 *
 * Key principles:
 * - Mastery is a continuous 0–100 score (not binary)
 * - Higher-tier correct answers contribute more to mastery
 * - Confidence builds with consistency over time
 * - History window prevents ancient attempts from dominating
 */

import { getTierForFamily } from '../content'

// ============================================================
// Types
// ============================================================

export interface FamilyAttempt {
  questionId: string
  isCorrect: boolean
  timestamp: number
  tier: number
  /** NEW: Rich answer payload (MeesterJay 4.1) */
  payload?: string // Serialized AnswerPayload
  /** NEW: Error type for didactic feedback (MeesterJay 4.1) */
  errorType?: string
  /** NEW: Numeric delta (minutes, cents, etc.) */
  delta?: number
  /**
   * Whether the learner consulted the cheatsheet / hints before answering.
   * When true, a correct answer is weighted at 50% K-factor in the ELO update
   * (see CHEATSHEET_HINT_SYSTEM_REDESIGN_2026.md §13). Wrong answers are NOT
   * additionally penalized — the goal is to honor independence, not to punish
   * help-seeking. No UI punishment is shown to the child.
   */
  helpUsed?: boolean
  /**
   * Whether the learner explicitly skipped this question via "Ik weet het
   * echt niet". Skipped attempts are gated by the UI (≥20s on question,
   * cheatsheet consulted, <2 skips/session) and are treated as a softer
   * incorrect signal: half K-factor, no tier adjustment, smaller confidence
   * decay. This honors the honest "ik weet het niet" without punishing it
   * as harshly as a guessed-wrong answer.
   */
  skipped?: boolean
}
export interface FamilyMasteryState {
  familyId: string
  masteryScore: number // 0–100 continuous
  attempts: number
  correct: number
  lastSeen: number // timestamp
  confidence: number // 0–1, stability indicator
  history: FamilyAttempt[] // last N attempts
}

// ============================================================
// Configuration
// ============================================================

/**
 * Frozen to prevent silent runtime mutation by misbehaving consumers or
 * stale test mocks (e.g. `CONFIG.BASE_LEARNING_RATE = 0` would silently
 * break every ELO update). Mirrors the hardening applied to
 * `MASTERY_DECAY_CONFIG` in `masteryDecay.ts`. — R6-2 fix, mei 2026.
 */
const CONFIG = Object.freeze({
  /** Maximum history entries to retain per family */
  HISTORY_WINDOW: 12,
  /** Base learning rate for ELO-style updates */
  BASE_LEARNING_RATE: 16,
  /** Learning rate multiplier when student has few attempts (faster early learning) */
  EARLY_LEARNING_MULTIPLIER: 2.0,
  /**
   * Number of attempts over which the early learning multiplier gradually
   * transitions to the base rate. Uses linear interpolation instead of a
   * hard cutoff to avoid a scoring discontinuity.
   *
   * v11.5: Changed from hard threshold (5) to gradual transition (8)
   * to avoid scoring discontinuity at the cutoff point.
   */
  EARLY_LEARNING_TRANSITION_ATTEMPTS: 8,
  /** Tier bonus: extra score per tier above the family's base tier for correct answers */
  TIER_BONUS_PER_LEVEL: 2,
  /** Tier penalty reduction: less penalty when failing a question above base tier */
  TIER_PENALTY_REDUCTION_PER_LEVEL: 1,
  /** Minimum mastery score (floor) */
  MIN_SCORE: 0,
  /** Maximum mastery score (ceiling) */
  MAX_SCORE: 100,
  /** Initial mastery score for new families */
  INITIAL_SCORE: 0,
  /** Confidence decay: how much confidence drops per wrong answer */
  CONFIDENCE_DECAY_WRONG: 0.15,
  /** Confidence growth: how much confidence grows per correct answer */
  CONFIDENCE_GROWTH_CORRECT: 0.08,
  /** Minimum confidence value */
  MIN_CONFIDENCE: 0,
  /** Maximum confidence value */
  MAX_CONFIDENCE: 1.0,
  /**
   * K-factor multiplier when the learner used help (cheatsheet/hints) before
   * a correct answer. Applies ONLY to correct answers — wrong answers are
   * never additionally penalized for using help. See CHEATSHEET_HINT_SYSTEM_REDESIGN_2026.md §13.
   */
  HELP_USED_K_MULTIPLIER: 0.5,
  /**
   * Confidence growth multiplier when help was used on a correct answer.
   * Mirrors the K-multiplier — independent recall builds more confidence
   * than guided recall.
   */
  HELP_USED_CONFIDENCE_MULTIPLIER: 0.5,
  /**
   * K-factor multiplier when the learner explicitly skipped. The mastery
   * signal still drops (skipping = "I don't know yet"), but only at half
   * the rate of a confident wrong answer. Pairs with SKIP_CONFIDENCE_DECAY
   * to keep frustration spirals from cratering the score.
   */
  SKIP_K_MULTIPLIER: 0.5,
  /**
   * Confidence decay applied on a skip. Smaller than CONFIDENCE_DECAY_WRONG
   * because an honest skip carries less misconception risk than a wrong
   * guess. We still nudge confidence down so the engine knows the family
   * isn't stable yet.
   */
  SKIP_CONFIDENCE_DECAY: 0.05,
} as const)

// ============================================================
// GROUP-SCOPED MASTERY KEYS (MeesterJay 4.1)
// ============================================================

/**
 * Generate a group-scoped mastery key.
 *
 * WHY GROUP-SCOPED KEYS EXIST:
 *
 * Mastery is tracked separately per school group (6, 7, 8) because:
 * 1. **Curriculum Isolation:** Each group has different learning objectives
 * 2. **Progress Independence:** A student's group 6 mastery shouldn't affect group 7
 * 3. **Adaptive Engine:** Question selection must respect group-specific progress
 * 4. **Multi-Year Support:** Students can work across multiple groups simultaneously
 *
 * EXAMPLE SCENARIO:
 * - Student masters "GET-BREUK-GEL-PLUS" in group 6 (basic fractions)
 * - In group 7, they encounter the same family at a higher tier
 * - Group 7 mastery starts fresh, allowing proper adaptive progression
 *
 * KEY FORMAT: "familyId_gX" where X is the group number (6, 7, or 8)
 *
 * This ensures progress in group 6 doesn't affect group 7/8.
 *
 * @param familyId - The canonical familyId
 * @param group - The school group (6, 7, or 8)
 * @returns Group-scoped key format: "familyId_gX"
 *
 * @example
 * getFamilyMasteryKey('GET-BREUK-GEL-PLUS', 6) // "GET-BREUK-GEL-PLUS_g6"
 * getFamilyMasteryKey('GET-BREUK-GEL-PLUS', 7) // "GET-BREUK-GEL-PLUS_g7"
 */
export function getFamilyMasteryKey(familyId: string, group: number): string {
  return `${familyId}_g${group}`
}

/**
 * Parse a group-scoped mastery key back into familyId and group.
 *
 * @param key - Group-scoped key (e.g., "GET-BREUK-GEL-PLUS_g6")
 * @returns Object with familyId and group, or null if invalid format
 */
export function parseMasteryKey(key: string): {
  familyId: string
  group: number
} | null {
  const match = key.match(/^(.+)_g(\d+)$/)
  if (!match) return null
  const familyId = match[1]
  const group = parseInt(match[2], 10)
  if (![6, 7, 8].includes(group)) return null
  return {
    familyId,
    group,
  }
}

/**
 * Check if a mastery key is group-scoped (new format).
 *
 * @param key - Mastery key to check
 * @returns True if key follows group-scoped format
 */
export function isGroupScopedKey(key: string): boolean {
  return /_g[678]$/.test(key)
}

// ============================================================
// Core Functions
// ============================================================

/**
 * Create a fresh FamilyMasteryState for a previously-unseen familyId.
 */
function createInitialState(familyId: string): FamilyMasteryState {
  return {
    familyId,
    masteryScore: CONFIG.INITIAL_SCORE,
    attempts: 0,
    correct: 0,
    lastSeen: 0,
    confidence: 0,
    history: [],
  }
}

/**
 * Compute the expected probability of a correct answer given current mastery.
 * Uses a logistic curve centered at 50 mastery.
 *
 * At mastery 0   → ~0.12 expected
 * At mastery 50  → 0.50 expected
 * At mastery 100 → ~0.88 expected
 */
function computeExpected(masteryScore: number): number {
  // Logistic function: 1 / (1 + 10^((50 - score) / 40))
  const exponent = (50 - masteryScore) / 40
  return 1 / (1 + Math.pow(10, exponent))
}

/**
 * Compute the effective learning rate based on attempt count.
 * New families learn faster (higher K-factor), experienced families are more stable.
 *
 * v11.5: Uses linear interpolation instead of a hard cutoff to eliminate
 * the scoring discontinuity where attempt #5 and #6 had very different
 * impact weights. Now transitions smoothly over 8 attempts:
 *   attempt 0 → K=32, attempt 4 → K=24, attempt 8 → K=16
 */
function getEffectiveLearningRate(attempts: number): number {
  const transition = CONFIG.EARLY_LEARNING_TRANSITION_ATTEMPTS
  if (attempts >= transition) return CONFIG.BASE_LEARNING_RATE

  // Linear interpolation: K starts at BASE * MULTIPLIER and decays to BASE
  const t = attempts / transition
  const multiplier =
    CONFIG.EARLY_LEARNING_MULTIPLIER -
    (CONFIG.EARLY_LEARNING_MULTIPLIER - 1) * t
  return CONFIG.BASE_LEARNING_RATE * multiplier
}

/**
 * Compute tier-based adjustment.
 * Correct answers on higher-tier questions earn bonus points.
 * Wrong answers on higher-tier questions are penalized less.
 */
function getTierAdjustment(
  questionTier: number,
  familyBaseTier: number,
  isCorrect: boolean,
): number {
  const tierDelta = questionTier - familyBaseTier
  if (tierDelta <= 0) return 0 // At or below base tier: no adjustment

  if (isCorrect) {
    // Bonus for answering harder questions correctly
    return tierDelta * CONFIG.TIER_BONUS_PER_LEVEL
  } else {
    // Reduced penalty for failing harder questions
    return tierDelta * CONFIG.TIER_PENALTY_REDUCTION_PER_LEVEL
  }
}

/**
 * Update confidence based on answer correctness.
 * Confidence represents how stable/reliable the mastery score is.
 * Grows slowly with correct answers, drops faster with wrong answers.
 */
function updateConfidence(
  currentConfidence: number,
  isCorrect: boolean,
  helpUsed: boolean = false,
): number {
  let delta: number
  if (isCorrect) {
    const growthMultiplier = helpUsed
      ? CONFIG.HELP_USED_CONFIDENCE_MULTIPLIER
      : 1
    delta = CONFIG.CONFIDENCE_GROWTH_CORRECT * growthMultiplier
  } else {
    delta = -CONFIG.CONFIDENCE_DECAY_WRONG
  }
  return Math.max(
    CONFIG.MIN_CONFIDENCE,
    Math.min(CONFIG.MAX_CONFIDENCE, currentConfidence + delta),
  )
}

/**
 * Core mastery update function.
 *
 * Uses an ELO-inspired formula:
 *   NewScore = OldScore + K × (Result - Expected) + TierAdjustment
 *
 * Where:
 * - K = effective learning rate (higher for new families)
 * - Result = 1 (correct) or 0 (wrong)
 * - Expected = logistic probability based on current mastery
 * - TierAdjustment = bonus/reduction based on question tier vs family base tier
 *
 * @param current - Current mastery state (undefined for first attempt)
 * @param familyId - The familyId being updated
 * @param isCorrect - Whether the answer was correct
 * @param questionId - The specific question answered
 * @param questionTier - The tier of the question answered
 * @param payload - Optional: serialized answer payload (MeesterJay 4.1)
 * @param errorType - Optional: error type for didactic feedback (MeesterJay 4.1)
 * @param delta - Optional: numeric delta (minutes, cents, etc.)
 * @param helpUsed - Optional: whether the learner used help (cheatsheet/hints)
 * @param skipped - Optional: whether the learner explicitly skipped
 * @returns Updated FamilyMasteryState
 */
export function updateFamilyMastery(
  current: FamilyMasteryState | undefined,
  familyId: string,
  isCorrect: boolean,
  questionId: string,
  questionTier: number,
  payload?: string,
  errorType?: string,
  delta?: number,
  helpUsed: boolean = false,
  skipped: boolean = false,
): FamilyMasteryState {
  const state = current ?? createInitialState(familyId)
  const now = Date.now()

  // Look up the family's base tier from taxonomy
  const familyBaseTier = getTierForFamily(familyId) ?? questionTier

  // ELO update
  const expected = computeExpected(state.masteryScore)
  const result = isCorrect ? 1 : 0
  const baseK = getEffectiveLearningRate(state.attempts)

  // Help-used discount: dampen K-factor for correct-with-help answers.
  // Wrong answers ignore helpUsed — using help and still getting it wrong is
  // a normal part of learning and should not be additionally penalized.
  const helpDiscounted = isCorrect && helpUsed
  let K = helpDiscounted ? baseK * CONFIG.HELP_USED_K_MULTIPLIER : baseK
  // Skip: softer signal than a wrong guess. Halve K and skip tier adjustment
  // entirely — we don't want a skipped tier-5 question to swing the score
  // either way. helpUsed is ignored on skip (you can't have "used help" if
  // you explicitly told us you don't know).
  if (skipped) {
    K = baseK * CONFIG.SKIP_K_MULTIPLIER
  }

  const tierAdj = skipped
    ? 0
    : getTierAdjustment(questionTier, familyBaseTier, isCorrect)
  let newScore = state.masteryScore + K * (result - expected)

  // Apply tier adjustment (skipped already zeroed it):
  if (helpDiscounted) {
    newScore += tierAdj * CONFIG.HELP_USED_K_MULTIPLIER
  } else {
    newScore += tierAdj
  }

  // Clamp to valid range
  newScore = Math.max(
    CONFIG.MIN_SCORE,
    Math.min(CONFIG.MAX_SCORE, Math.round(newScore * 100) / 100),
  )

  // Update history (with rich data)
  const newAttempt: FamilyAttempt = {
    questionId,
    isCorrect,
    timestamp: now,
    tier: questionTier,
    payload,
    errorType,
    delta,
    helpUsed: helpUsed || undefined,
    skipped: skipped || undefined,
  }
  const newHistory = [...state.history, newAttempt].slice(
    -CONFIG.HISTORY_WINDOW,
  )

  // Update confidence — skip path uses a smaller decay than a regular wrong.
  let newConfidence: number
  if (skipped) {
    newConfidence = Math.max(
      CONFIG.MIN_CONFIDENCE,
      Math.min(
        CONFIG.MAX_CONFIDENCE,
        state.confidence - CONFIG.SKIP_CONFIDENCE_DECAY,
      ),
    )
  } else {
    newConfidence = updateConfidence(
      state.confidence,
      isCorrect,
      helpDiscounted,
    )
  }
  return {
    familyId,
    masteryScore: newScore,
    attempts: state.attempts + 1,
    correct: state.correct + (isCorrect ? 1 : 0),
    lastSeen: now,
    confidence: newConfidence,
    history: newHistory,
  }
}
